import React from 'react';
import { motion } from 'framer-motion';
import { PageBanner } from '../components/PageBanner';
import { BookingCTA } from '../components/BookingCTA';
import { Button } from '../components/ui/Button';
import { SparklesIcon, HeartIcon, DollarSignIcon, DoorOpenIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
const features = [{
  icon: SparklesIcon,
  title: 'Expert Stylists',
  description: 'Years of experience in African hair braiding with a passion for creating beautiful, protective styles.'
}, {
  icon: HeartIcon,
  title: 'Premium Products',
  description: 'We use only the best natural hair care products to ensure healthy, long-lasting styles.'
}, {
  icon: DoorOpenIcon,
  title: 'Walk-ins Welcome',
  description: "No appointment needed! Come anytime during our business hours and we'll take care of you."
}, {
  icon: DollarSignIcon,
  title: 'Affordable Pricing',
  description: 'Quality styles at competitive prices. Plus, enjoy our ongoing promotions and discounts.'
}];
export function AboutPage() {
  return <>
      <PageBanner title="About Us" subtitle="Honoring tradition while elevating modern style" backgroundImage="https://cdn.magicpatterns.com/uploads/dWCkh2pKNY8WmbWaAv4P5x/ChatGPT_Image_Mar_10,_2026,_11_45_41_AM.png" data-id="element-365" />

      {/* Who We Are Section */}
      <section className="py-8 md:py-20 bg-white" data-id="element-366">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-367">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center" data-id="element-368">
            <span className="text-burgundy font-medium tracking-[0.2em] uppercase text-sm mb-4 block" data-id="element-369">
              Who We Are
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-jet mb-8" data-id="element-370">
              Welcome to Beni's African Hair Braiding
            </h2>
            <div className="space-y-6 text-jet/70 text-lg leading-relaxed" data-id="element-371">
              <p data-id="element-372">
                Step into a space where cultural heritage meets contemporary
                elegance. Located at 2768 Crain Hwy, Waldorf, MD 20601, Beni's
                African Hair Braiding is a sanctuary dedicated to the ancient,
                beautiful art of protective styling.
              </p>
              <p data-id="element-373">
                We take immense pride in our roots, seamlessly blending
                generations of traditional braiding techniques with modern
                trends to create styles that are as unique as you are. Our
                skilled stylists are passionate about celebrating your
                individuality, working meticulously to enhance your natural
                beauty.
              </p>
              <p data-id="element-374">
                Whether you're looking for classic box braids, trendy knotless
                braids, elegant locs, or any protective style, our team is here
                to bring your vision to life. We believe in creating a relaxing,
                soothing, and refreshing salon experience for every client.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-8 md:py-20 bg-beige" data-id="element-375">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-376">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-377">
            <span className="text-burgundy font-medium tracking-[0.2em] uppercase text-sm mb-4 block" data-id="element-378">
              Why Choose Us
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-jet" data-id="element-379">
              The Beni's Difference
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-380">
            {features.map((feature, index) => <motion.div key={feature.title} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white p-8 rounded-sm shadow-sm text-center" data-id="element-381">
                <div className="w-16 h-16 bg-golden/10 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-382">
                  <feature.icon className="w-8 h-8 text-golden" data-id="element-383" />
                </div>
                <h3 className="text-xl font-heading font-bold text-jet mb-3" data-id="element-384">
                  {feature.title}
                </h3>
                <p className="text-jet/60 text-sm leading-relaxed" data-id="element-385">
                  {feature.description}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-8 md:py-20 bg-white" data-id="element-386">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-387">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12" data-id="element-388">
            <span className="text-burgundy font-medium tracking-[0.2em] uppercase text-sm mb-4 block" data-id="element-389">
              Visit Us
            </span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-jet" data-id="element-390">
              Come See Us Today
            </h2>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-jet rounded-sm overflow-hidden text-center" data-id="element-391">
            <div className="w-full aspect-[16/8] overflow-hidden" data-id="element-392">
              <img src="https://cdn.magicpatterns.com/uploads/oX7dZRbThRBQNp4vhE5uRX/ChatGPT_Image_Mar_9,_2026,_05_23_48_PM.png" alt="Beni's African Braiding storefront at 2768 Crain Hwy, Waldorf, MD" className="w-full h-full object-cover" data-id="element-393" />
            </div>
            <div className="p-8 md:p-12" data-id="element-394">
              <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16 mb-8" data-id="element-395">
                <div className="flex items-center gap-4" data-id="element-396">
                  <div className="w-12 h-12 bg-golden/20 rounded-full flex items-center justify-center" data-id="element-397">
                    <MapPinIcon className="w-6 h-6 text-golden" data-id="element-398" />
                  </div>
                  <div className="text-left" data-id="element-399">
                    <p className="text-white font-medium" data-id="element-400">2768 Crain Hwy</p>
                    <p className="text-white/70 text-sm" data-id="element-401">Waldorf, MD 20601</p>
                    <p className="text-white/50 text-xs" data-id="element-402">
                      (Beside Heritage Church International)
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4" data-id="element-403">
                  <div className="w-12 h-12 bg-golden/20 rounded-full flex items-center justify-center" data-id="element-404">
                    <PhoneIcon className="w-6 h-6 text-golden" data-id="element-405" />
                  </div>
                  <div className="text-left" data-id="element-406">
                    <p className="text-white font-medium" data-id="element-407">301-710-6221</p>
                    <p className="text-white font-medium" data-id="element-408">301-385-8034</p>
                  </div>
                </div>
              </div>
              <a href="tel:3013858034" data-id="element-409">
                <Button variant="primary" size="lg" className="bg-golden text-jet hover:bg-gold-muted" data-id="element-410">
                  Call Now to Book
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <BookingCTA data-id="element-411" />
    </>;
}